import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTr } from "@/i18n/translator";
import {
  LifeBuoy, Plus, Send, Paperclip, Bot, User as UserIcon, Shield,
  AlertCircle, CheckCircle2, X, Clock, Image as ImageIcon, FileText,
} from "lucide-react";

const STATUS_LABEL = {
  open: "ABIERTO",
  in_progress: "EN PROGRESO",
  resolved: "RESUELTO",
  closed: "CERRADO",
  escalated: "ESCALADO · HUMANO",
};
const STATUS_COLOR = {
  open: "bg-[#E53935] text-white",
  in_progress: "bg-[#FFD600]",
  resolved: "bg-[#0A0A0A] text-[#FFD600]",
  closed: "bg-[#E5E5E5]",
  escalated: "bg-[#E53935] text-white",
};
const CATS = [
  { id: "general", label: "General" },
  { id: "billing", label: "Facturación" },
  { id: "bug", label: "Bug / Error" },
  { id: "feature", label: "Mejora / Idea" },
];

export default function UserSupport() {
  const tr = useTr();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ subject: "", message: "", category: "general" });
  const [newFiles, setNewFiles] = useState([]);
  const [creating, setCreating] = useState(false);
  const pollRef = useRef(null);
  const threadRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/support/my-tickets");
    setTickets(data);
  };

  useEffect(() => { load(); }, []);

  // Refresh open ticket every 5s (for AI replies)
  useEffect(() => {
    if (!sel) { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; return; }
    const loadOne = async () => {
      try {
        const { data } = await api.get(`/support/tickets/${sel}`);
        setDetail(data);
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
      } catch {}
    };
    loadOne();
    pollRef.current = setInterval(loadOne, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sel]);

  const createTicket = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("name", user?.name || "");
      fd.append("email", user?.email || "");
      fd.append("subject", newForm.subject);
      fd.append("message", newForm.message);
      fd.append("category", newForm.category);
      newFiles.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/support/tickets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(tr("Ticket creado. El asistente está revisándolo."));
      setShowNew(false);
      setNewForm({ subject: "", message: "", category: "general" });
      setNewFiles([]);
      await load();
      setSel(data.id);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Error");
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("content", reply);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/support/tickets/${sel}/reply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDetail(data);
      setReply("");
      setFiles([]);
      await load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    if (!window.confirm(tr("¿Quieres que un humano revise tu caso?"))) return;
    try {
      const { data } = await api.post(`/support/tickets/${sel}/escalate`);
      setDetail(data);
      await load();
      toast.success(tr("Escalado. Te responderemos pronto."));
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Error");
    }
  };

  const markResolved = async () => {
    try {
      const { data } = await api.post(`/support/tickets/${sel}/close`);
      setDetail(data);
      await load();
      toast.success(tr("Ticket marcado como resuelto"));
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="user-support-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("SOPORTE · TUS TICKETS")}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
            <LifeBuoy className="w-10 h-10 text-[#E53935]" />
            {tr("Centro de ayuda")}
          </h1>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-3 max-w-2xl">
            {tr("Tu asistente de soporte revisa cada caso al instante. Si no queda resuelto, puedes pedir hablar con un humano.")}
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="bg-[#E53935] text-white px-5 py-3 font-mono-mark text-xs border border-[#E53935] hover-brutal flex items-center gap-2"
          data-testid="new-ticket-btn"
        >
          <Plus className="w-4 h-4" /> {tr("NUEVO TICKET")}
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lista */}
        <div className="lg:col-span-1 bg-white border border-[#0A0A0A]">
          <div className="px-4 py-3 border-b border-[#0A0A0A] font-mono-mark text-[10px] text-[#5C5C5C]">
            {tickets.length} {tr("TICKET(S)")}
          </div>
          {tickets.length === 0 ? (
            <div className="p-6 text-center font-mono-data text-sm text-[#5C5C5C]">
              {tr("Aún no tienes tickets.")}
            </div>
          ) : (
            <ul>
              {tickets.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => setSel(t.id)}
                    className={`w-full text-left p-4 border-b border-[#0A0A0A]/20 hover:bg-[#F4F4F0] ${sel === t.id ? "bg-[#F4F4F0]" : ""}`}
                    data-testid={`ticket-item-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display text-sm truncate">{t.subject}</div>
                      <span className={`font-mono-mark text-[9px] px-2 py-0.5 shrink-0 ${STATUS_COLOR[t.status] || "bg-[#E5E5E5]"}`}>
                        {STATUS_LABEL[t.status] || t.status}
                      </span>
                    </div>
                    <div className="font-mono-data text-xs text-[#5C5C5C] mt-1 truncate">{t.message?.slice(0, 80)}</div>
                    <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-1">
                      {new Date(t.created_at).toLocaleDateString("es-MX")} · {(t.messages?.length || 1)} {tr("msg")}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detalle/chat */}
        <div className="lg:col-span-2 bg-white border border-[#0A0A0A] min-h-[500px] flex flex-col">
          {!detail ? (
            <div className="flex-1 flex items-center justify-center font-mono-data text-sm text-[#5C5C5C]">
              {tickets.length === 0 ? tr("Crea un ticket para empezar") : tr("Selecciona un ticket")}
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-[#0A0A0A] flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-mono-mark text-[10px] text-[#5C5C5C]">#{detail.id.slice(0, 8)} · {CATS.find(c => c.id === detail.category)?.label || detail.category}</div>
                  <div className="font-display text-xl mt-1 break-words">{detail.subject}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`font-mono-mark text-[10px] px-2 py-1 ${STATUS_COLOR[detail.status] || "bg-[#E5E5E5]"}`}>
                    {STATUS_LABEL[detail.status] || detail.status}
                  </span>
                  {detail.status !== "resolved" && detail.status !== "closed" && (
                    <button onClick={markResolved} className="font-mono-mark text-[10px] bg-white border border-[#0A0A0A] px-3 py-1.5 flex items-center gap-1" data-testid="mark-resolved">
                      <CheckCircle2 className="w-3 h-3" /> {tr("RESUELTO")}
                    </button>
                  )}
                </div>
              </div>

              {/* Thread */}
              <div ref={threadRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#F4F4F0]" data-testid="ticket-thread">
                {(detail.messages || []).map((m, i) => (
                  <MessageBubble key={i} m={m} />
                ))}
                {detail.ai_attempts < 3 && detail.status === "in_progress" && detail.messages?.[detail.messages.length - 1]?.sender === "user" && (
                  <div className="flex items-center gap-2 text-[#5C5C5C] font-mono-mark text-[9px]">
                    <Bot className="w-3 h-3 animate-pulse" /> {tr("ASISTENTE ESCRIBIENDO…")}
                  </div>
                )}
              </div>

              {/* Escalar banner */}
              {(detail.can_escalate || detail.ai_attempts >= 2) && !detail.escalated && detail.status !== "closed" && detail.status !== "resolved" && (
                <div className="px-5 py-3 border-t border-[#E53935] bg-[#FFD600] flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-mono-data text-xs flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-[#E53935]" />
                    {tr("¿No te resolvió el asistente?")}
                  </div>
                  <button onClick={escalate} className="font-mono-mark text-[10px] bg-[#0A0A0A] text-white px-4 py-2 border border-[#0A0A0A]" data-testid="escalate-btn">
                    {tr("HABLAR CON HUMANO")} →
                  </button>
                </div>
              )}
              {detail.escalated && (
                <div className="px-5 py-3 border-t border-[#0A0A0A] bg-[#0A0A0A] text-[#FFD600] font-mono-mark text-[10px] flex items-center gap-2">
                  <Shield className="w-3 h-3" /> {tr("ESCALADO A AGENTE HUMANO. TE RESPONDEREMOS POR EMAIL.")}
                </div>
              )}

              {/* Input reply */}
              {detail.status !== "closed" && (
                <form onSubmit={sendReply} className="border-t border-[#0A0A0A] p-3 space-y-2">
                  {files.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {files.map((f, i) => (
                        <div key={i} className="flex items-center gap-1 bg-[#FFD600] px-2 py-1 font-mono-mark text-[9px]">
                          <Paperclip className="w-3 h-3" /> {f.name}
                          <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <label className="shrink-0 cursor-pointer bg-white border border-[#0A0A0A] px-3 py-2 hover:bg-[#F4F4F0]" data-testid="reply-attach-label">
                      <Paperclip className="w-4 h-4" />
                      <input
                        type="file"
                        multiple
                        hidden
                        accept="image/*,application/pdf,text/plain"
                        onChange={(e) => setFiles([...(files || []), ...Array.from(e.target.files || [])].slice(0, 3))}
                        data-testid="reply-attach"
                      />
                    </label>
                    <input
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder={tr("Escribe tu respuesta…")}
                      className="flex-1 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
                      data-testid="reply-input"
                    />
                    <button type="submit" disabled={sending || !reply.trim()} className="bg-[#E53935] text-white px-4 py-2 border border-[#E53935] font-mono-mark text-[10px] flex items-center gap-1 disabled:opacity-50" data-testid="reply-send">
                      <Send className="w-3 h-3" /> {sending ? "..." : "ENVIAR"}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>

      {/* Nuevo ticket modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <form onSubmit={createTicket} onClick={(e) => e.stopPropagation()} className="bg-white border border-[#0A0A0A] brutal-shadow max-w-xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="new-ticket-modal">
            <div className="flex items-start justify-between">
              <h2 className="font-display text-2xl">{tr("Nuevo ticket")}</h2>
              <button type="button" onClick={() => setShowNew(false)}><X className="w-5 h-5" /></button>
            </div>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">CATEGORÍA</span>
              <select value={newForm.category} onChange={(e) => setNewForm({ ...newForm, category: e.target.value })} className="w-full mt-2 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="new-ticket-category">
                {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">ASUNTO</span>
              <input required minLength={3} value={newForm.subject} onChange={(e) => setNewForm({ ...newForm, subject: e.target.value })} className="w-full mt-2 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="new-ticket-subject" />
            </label>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">MENSAJE</span>
              <textarea required minLength={5} rows={5} value={newForm.message} onChange={(e) => setNewForm({ ...newForm, message: e.target.value })} className="w-full mt-2 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" data-testid="new-ticket-message" />
            </label>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ADJUNTOS (MÁX 3 · 2MB C/U)")}</span>
              <input type="file" multiple accept="image/*,application/pdf,text/plain" onChange={(e) => setNewFiles(Array.from(e.target.files || []).slice(0, 3))} className="w-full mt-2 font-mono-data text-xs" data-testid="new-ticket-files" />
              {newFiles.length > 0 && (
                <div className="mt-2 font-mono-mark text-[9px] text-[#5C5C5C]">
                  {newFiles.length} {tr("archivo(s)")}: {newFiles.map(f => f.name).join(", ")}
                </div>
              )}
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="font-mono-mark text-[10px] bg-white border border-[#0A0A0A] px-4 py-2">CANCELAR</button>
              <button type="submit" disabled={creating} className="font-mono-mark text-[10px] bg-[#E53935] text-white border border-[#E53935] px-4 py-2 disabled:opacity-50" data-testid="new-ticket-submit">
                {creating ? "ENVIANDO…" : "CREAR TICKET"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ m }) {
  const isAi = m.sender === "ai";
  const isAdmin = m.sender === "admin";
  const Icon = isAi ? Bot : isAdmin ? Shield : UserIcon;
  const bg = isAi ? "bg-white" : isAdmin ? "bg-[#0A0A0A] text-[#FFD600]" : "bg-[#FFD600]";
  const align = m.sender === "user" ? "ml-auto" : "mr-auto";
  const label = isAi ? "EQUIPO CIA" : isAdmin ? "ADMIN" : "TÚ";

  return (
    <div className={`max-w-[85%] ${align} ${bg} border border-[#0A0A0A] p-3`}>
      <div className="font-mono-mark text-[9px] mb-2 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label} · {m.created_at ? new Date(m.created_at).toLocaleString("es-MX") : ""}
      </div>
      <div className="font-mono-data text-sm whitespace-pre-wrap">{m.content}</div>
      {m.attachments && m.attachments.length > 0 && (
        <div className="mt-2 flex gap-2 flex-wrap">
          {m.attachments.map((a, i) => (
            <a
              key={i}
              href={`data:${a.mime};base64,${a.b64}`}
              download={a.filename}
              className="flex items-center gap-1 bg-white border border-[#0A0A0A]/40 px-2 py-1 font-mono-mark text-[9px] text-[#0A0A0A]"
            >
              {a.mime?.startsWith("image/") ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
              {a.filename}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
